import { normalizeCompanyName } from './normalise.js';
const token = (value) => String(value || '').toLowerCase().normalize('NFKD').replace(/[^a-z0-9]/g, '');
export function detectEmailPattern(contact) {
    const email = String(contact.email || '').trim().toLowerCase();
    const at = email.lastIndexOf('@');
    if (at < 1)
        return null;
    const local = email.slice(0, at), domain = email.slice(at + 1);
    const f = token(contact.firstName), l = token(contact.lastName);
    if (!f || !l || !domain)
        return null;
    const candidates = {
        'first.last': `${f}.${l}`, 'firstlast': `${f}${l}`, 'first_initial.last': `${f[0]}.${l}`, 'first.last_initial': `${f}.${l[0]}`, 'first': f, 'last.first': `${l}.${f}`
    };
    for (const [pattern, value] of Object.entries(candidates))
        if (local === value)
            return { pattern, domain };
    return null;
}
export function inferConfirmedPattern(contacts, companyId, companyName) {
    const targetName = normalizeCompanyName(companyName || '');
    const evidence = contacts.filter(c => (c.companyId === companyId || (targetName && normalizeCompanyName(c.companyName) === targetName)) && c.emailVerification === 'verified' && !!c.email).map(c => ({ contact: c, detected: detectEmailPattern(c) })).filter(x => x.detected);
    const groups = new Map();
    for (const item of evidence) {
        const d = item.detected;
        const key = `${d.pattern}|${d.domain}`;
        const row = groups.get(key) || { pattern: d.pattern, domain: d.domain, ids: [] };
        row.ids.push(item.contact.id);
        groups.set(key, row);
    }
    const ranked = [...groups.values()].sort((a, b) => b.ids.length - a.ids.length);
    if (!ranked.length || ranked[0].ids.length < 2)
        return { confirmed: false, verifiedSamples: ranked[0]?.ids.length || 0, sampleContactIds: ranked[0]?.ids || [] };
    // Any conflicting verified pattern/domain invalidates the assumption. A company
    // format is only considered confirmed when all detectable verified evidence agrees.
    if (ranked.length > 1)
        return { confirmed: false, verifiedSamples: ranked[0].ids.length, sampleContactIds: ranked[0].ids };
    return { confirmed: true, pattern: ranked[0].pattern, domain: ranked[0].domain, verifiedSamples: ranked[0].ids.length, sampleContactIds: ranked[0].ids };
}
export function predictEmail(contact, pattern, domain) {
    const f = token(contact.firstName), l = token(contact.lastName);
    if (!f || !l || !domain)
        return '';
    const local = { 'first.last': `${f}.${l}`, 'firstlast': `${f}${l}`, 'first_initial.last': `${f[0]}.${l}`, 'first.last_initial': `${f}.${l[0]}`, 'first': f, 'last.first': `${l}.${f}` };
    return `${local[pattern]}@${domain}`;
}
/**
 * Internal confidence indicator for outreach triage. This is deliberately not a
 * deliverability probability: even a provider-verified address can later bounce.
 */
export function emailConfidence(contact) {
    const hasEmail = !!String(contact.email || '').trim();
    if (contact.emailVerification === 'bounced')
        return { score: 0, label: 'Bounced', detail: 'This address has bounced and should not be reused as pattern evidence.' };
    if (!hasEmail)
        return { score: 0, label: 'No email', detail: 'No email address is currently resolved.' };
    if (contact.emailVerification === 'verified')
        return { score: 98, label: 'Individually verified', detail: 'Verified through Prospeo. This is not a delivery guarantee.' };
    if (contact.emailVerification === 'pattern_confirmed') {
        const match = String(contact.emailSource || '').match(/from\s+(\d+)\s+Prospeo-verified/i);
        const samples = Math.max(2, Number(match?.[1] || 2));
        const score = samples >= 4 ? 95 : samples === 3 ? 92 : 88;
        return { score, label: 'Confirmed company format', detail: `Derived from ${samples} agreeing Prospeo-verified company emails.` };
    }
    if (contact.emailVerification === 'pattern_predicted')
        return { score: 65, label: 'Predicted company format', detail: 'Pattern-predicted only; not eligible for protected automatic scheduling.' };
    return { score: 45, label: 'Known, unverified', detail: 'An email is known but has not been individually verified or confirmed from company-format evidence.' };
}
